/**
 * An IndexTree is a trie where values are keyed by a specified path accessor.
 *
 * Traditionally, the values of a trie are strings and the path is the string's characters, but any type with
 * an iterable path can be used here.
 *
 * This data structure is optimized for use in React trees that want to leverage React.memo / PureComponent
 * where the component hierarchy matches the trie hierarchy, i.e. a child component can take the relevant
 * subtrie as a prop.
 *
 * Example:
 *   const values = [
 *     {path: ['a', 1], name: 'A1 part 1'},
 *     {path: ['a', 1], name: 'A1 part 2'},
 *     {path: ['a', 2], name: 'A2'},
 *     {path: ['b', 1], name: 'B1'}
 *   ];
 *
 *   // Constructing an index tree
 *   const indexTree = buildIndexTree(values, (item) => item.path)
 *
 *   // Traversing the tree one path component at a time
 *   const indexTreeA = getSubtreeAtIndex(indexTree, 'a');
 *   const indexTreeA1 = getSubtreeAtIndex(indexTreeA, 1);
 *
 *   // Accessing the items returns [{path: ['a', 1], name: 'A1 part 1'}, {path: ['a', 1], name: 'A1 part 2'}]
 *   getValuesAtIndexTreeRoot(indexTreeA1)
 *
 */
export interface IndexTree<T> {
  // NB: Map was considered as the data structure, but that would prevent IndexTree from being safe for use in
  // React props, as its common to use deep equal checks on props and sadly Map does not work with isEqual.
  nodes: {[index: string]: IndexTree<T>};
  values: Array<T>;
}

export type MaybeIndexTree<T> = IndexTree<T> | null;

export type IndexTreeIndex = number | string;

const NO_VALUES: [] = [];

function toKey(index: IndexTreeIndex): string {
  // Protect against the pathological cases where index might be "constructor" or "__proto__"
  return `key:${index}`;
}

function fromKey(key: string): IndexTreeIndex {
  return key.slice(4) as IndexTreeIndex;
}

function makeEmptyIndexTree<T>(): IndexTree<T> {
  // NB: these will be mutated, so nodes and values must be locally created objects.
  return {nodes: {}, values: []};
}

export function buildIndexTree<T>(
  values: Array<T>,
  getPath: (value: T) => Iterable<IndexTreeIndex>
): IndexTree<T> {
  const tree = makeEmptyIndexTree<T>();
  values.forEach((value) => {
    let subtree = tree;
    for (const index of getPath(value)) {
      const key = toKey(index);
      if (!subtree.nodes[key]) {
        subtree.nodes[key] = makeEmptyIndexTree<T>();
      }
      subtree = subtree.nodes[key];
    }
    subtree.values.push(value);
  });
  return tree;
}

export function getSubtreeAtIndex<T>(root: MaybeIndexTree<T>, index: IndexTreeIndex): MaybeIndexTree<T> {
  return root?.nodes[toKey(index)] ?? null;
}

export function getValuesAtIndexTreeRoot<T>(root: MaybeIndexTree<T>): Array<T> {
  return root?.values ?? NO_VALUES;
}

export function getIndexesfromIndexTreeRoot(root: MaybeIndexTree<unknown>): Array<IndexTreeIndex> {
  return Object.keys(root?.nodes ?? []).map((key) => fromKey(key));
}

export function isIndexTreeEmpty(root: MaybeIndexTree<unknown>): boolean {
  return root == null || (root.values.length === 0 && Object.keys(root.nodes).length === 0);
}
